import React, { useContext, useMemo, useState } from "react";
import { CartContext } from "./CartContext";
import "../css/checkout.css";
import productData, { formatPrice, siteConfig } from "../Datas/productData";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiCheck, FiLock, FiPackage, FiTruck } from "react-icons/fi";

const supportedCountries = [
  "Australia", "Austria", "Belgium", "Canada", "Czech Republic", "Denmark",
  "Finland", "France", "Germany", "Ireland", "Italy", "Japan",
  "Luxembourg", "Malaysia", "Netherlands", "New Zealand", "Norway", "Poland",
  "Portugal", "Singapore", "South Korea", "Spain", "Sweden", "Switzerland",
  "Taiwan", "Thailand", "United Kingdom", "United States", "Vietnam",
];

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  address2: "",
  city: "",
  region: "",
  postcode: "",
  country: "Germany",
  notes: "",
  terms: false,
};

const createOrderNumber = () => {
  const values = new Uint32Array(2);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(values);
  else {
    values[0] = Math.floor(Math.random() * 1_000_000_000);
    values[1] = Math.floor(Math.random() * 1_000_000_000);
  }
  return `${Date.now()}${values[0]}${values[1]}`.slice(0, 30);
};

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const detailedItems = useMemo(
    () => cartItems
      .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
      .filter((item) => item.product),
    [cartItems]
  );
  const subtotal = detailedItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shipping = siteConfig.shipping.standard;
  const total = subtotal + shipping;

  const update = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Enter a valid email address.";
    if (!form.firstName.trim()) next.firstName = "Enter your first name.";
    if (!form.lastName.trim()) next.lastName = "Enter your last name.";
    if (!form.address.trim()) next.address = "Enter a delivery address.";
    if (!form.city.trim()) next.city = "Enter a city.";
    if (!form.region.trim()) next.region = "Enter a state, province or region.";
    if (!form.postcode.trim()) next.postcode = "Enter a postal code.";
    if (!supportedCountries.includes(form.country)) next.country = "Choose a supported country or region.";
    if (!form.terms) next.terms = "Accept the terms before continuing to payment.";
    return next;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      window.requestAnimationFrame(() => document.querySelector(".checkout-page [aria-invalid='true']")?.focus());
      return;
    }

    setIsSubmitting(true);
    const origin = window.location.origin;
    const paymentUrl = new URL("/create_payment.php", origin);
    const successUrl = new URL("/payment?state=success", origin);
    const cancelUrl = new URL("/payment?state=cancel", origin);
    const orderNo = createOrderNumber();
    window.localStorage.setItem("poodlivingPendingOrder", JSON.stringify({ orderNo, total, email: form.email.trim() }));
    paymentUrl.search = new URLSearchParams({
      orderNo,
      amount: total.toFixed(2),
      currency: siteConfig.currency,
      email: form.email.trim(),
      success_url: successUrl.toString(),
      cancel_url: cancelUrl.toString(),
      title: detailedItems[0].product.name,
    }).toString();
    window.location.assign(paymentUrl.toString());
  };

  if (detailedItems.length === 0) {
    return (
      <main className="checkout-page checkout-page--empty">
        <FiPackage />
        <h1>Your checkout is empty</h1>
        <p>Add something to your cart before continuing to delivery and payment.</p>
        <Link to="/shop">Browse products</Link>
      </main>
    );
  }

  const field = (name, label, options = {}) => (
    <label className={`checkout-page__field ${options.className || ""}`}>
      <span>{label}{options.required && " *"}</span>
      <input
        type={options.type || "text"}
        name={name}
        value={form[name]}
        onChange={update}
        autoComplete={options.autoComplete}
        inputMode={options.inputMode}
        placeholder={options.placeholder}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `${name}-error` : undefined}
      />
      {errors[name] && <small id={`${name}-error`} role="alert">{errors[name]}</small>}
    </label>
  );

  return (
    <main className="checkout-page">
      <div className="checkout-page__top">
        <Link to="/cartpage"><FiArrowLeft /> Back to cart</Link>
        <strong><img src="/head/poodliving-logo.svg" alt={siteConfig.brandName} /></strong>
        <span><FiLock /> Secure checkout</span>
      </div>

      <form className="checkout-page__form" onSubmit={handleSubmit} noValidate>
        <div className="checkout-page__content">
          <section className="checkout-page__section" aria-labelledby="contact-heading">
            <div className="checkout-page__section-heading"><span>1</span><div><p>Where should order updates go?</p><h1 id="contact-heading">Contact</h1></div></div>
            {field("email", "Email address", { type: "email", required: true, autoComplete: "email", inputMode: "email", placeholder: "you@example.com" })}
          </section>

          <section className="checkout-page__section" aria-labelledby="delivery-address-heading">
            <div className="checkout-page__section-heading"><span>2</span><div><p>Where should we send it?</p><h2 id="delivery-address-heading">Delivery address</h2></div></div>
            <div className="checkout-page__row">
              {field("firstName", "First name", { required: true, autoComplete: "given-name" })}
              {field("lastName", "Last name", { required: true, autoComplete: "family-name" })}
            </div>
            {field("company", "Company (optional)", { autoComplete: "organization" })}
            {field("address", "Address", { required: true, autoComplete: "address-line1" })}
            {field("address2", "Apartment, suite, etc. (optional)", { autoComplete: "address-line2" })}
            <div className="checkout-page__row">
              {field("city", "City", { required: true, autoComplete: "address-level2" })}
              {field("region", "State / province / region", { required: true, autoComplete: "address-level1" })}
            </div>
            <div className="checkout-page__row">
              {field("postcode", "Postal code", { required: true, autoComplete: "postal-code" })}
              <label className="checkout-page__field">
                <span>Country / region *</span>
                <select name="country" value={form.country} onChange={update} aria-invalid={Boolean(errors.country)}>
                  {supportedCountries.map((country) => <option key={country}>{country}</option>)}
                </select>
                {errors.country && <small role="alert">{errors.country}</small>}
              </label>
            </div>
          </section>

          <section className="checkout-page__section" aria-labelledby="payment-heading">
            <div className="checkout-page__section-heading"><span>3</span><div><p>Review before continuing</p><h2 id="payment-heading">Payment</h2></div></div>
            <div className="checkout-page__hosted-payment">
              <strong><FiLock /> Secure hosted payment</strong>
              <p>After you select Pay now, you will continue to the payment provider. Card or wallet details are entered there and are never collected on this form.</p>
            </div>
            <label className="checkout-page__field"><span>Order note (optional)</span><textarea name="notes" value={form.notes} onChange={update} rows="3" placeholder="Delivery notes or a gift message" /></label>
            <label className="checkout-page__check">
              <input type="checkbox" name="terms" checked={form.terms} onChange={update} aria-invalid={Boolean(errors.terms)} aria-describedby={errors.terms ? "terms-error" : undefined} />
              I agree to the <Link to="/terms" target="_blank" rel="noreferrer">terms and conditions</Link> and <Link to="/privacy" target="_blank" rel="noreferrer">privacy policy</Link>.
            </label>
            {errors.terms && <small id="terms-error" className="checkout-page__check-error" role="alert">{errors.terms}</small>}
          </section>
        </div>

        <aside className="checkout-page__summary">
          <h2>Your order</h2>
          <div className="checkout-page__products">
            {detailedItems.map((item) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div><img src={item.product.image} alt={item.product.name} /><span>{item.quantity}</span></div>
                <p><strong>{item.product.name}</strong><small>{item.size} · {item.color}</small></p>
                <b>{formatPrice(item.product.price * item.quantity)}</b>
              </article>
            ))}
          </div>
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div><dt>Tracked shipping</dt><dd>{formatPrice(shipping)}</dd></div>
          </dl>
          <div className="checkout-page__total"><span>Total <small>USD</small></span><strong>{formatPrice(total)}</strong></div>
          <button type="submit" disabled={isSubmitting}>{isSubmitting ? "Opening payment…" : <><FiCheck /> Pay now</>}</button>
          <p><FiTruck /> Tracked shipping is a fixed {formatPrice(shipping)} per order.</p>
        </aside>
      </form>
    </main>
  );
};

export default CheckOut;
